import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  DesignRequestFormFields,
  FORM_VERSION,
  blankSku,
  emptyDesignRequestForm,
} from "../components/DesignRequestForm";
import { GAMER_LOGO_SRC } from "../gamerLogoData";
import "../App.css";
import "../CustomerIntakeBrand.css";

const API = process.env.REACT_APP_BACKEND_URL;
const LIQUID_SAMPLE_REMINDER = "A liquid sample is required for all submitted SKU(s). Please ensure one is included with each submission.";

const contactSampleFields = (form) => ({
  sample_recipient_name: form.contact_name || "",
  sample_recipient_email: form.contact_email || "",
  sample_ship_to_street: form.contact_street || "",
  sample_ship_to_city: form.contact_city || "",
  sample_ship_to_state: form.contact_state || "",
  sample_ship_to_zip: form.contact_zip || "",
});

const GamerBrandHeader = () => (
  <header className="gamer-brand-header">
    <div className="gamer-brand-header-inner">
      <a href="/" className="gamer-brand-home-link" aria-label="Return to InkSight home">
        <img src={GAMER_LOGO_SRC} alt="Gamer Packaging" className="gamer-brand-logo" />
      </a>
      <div className="gamer-brand-context">
        <strong>Graphics Design Requests</strong>
        Customer-facing artwork intake portal
      </div>
    </div>
  </header>
);

const GamerBrandFooter = () => (
  <footer className="gamer-brand-footer">
    <div className="gamer-brand-footer-inner">
      <a href="/" className="gamer-brand-home-link" aria-label="Return to InkSight home">
        <img src={GAMER_LOGO_SRC} alt="Gamer Packaging" className="gamer-brand-footer-logo" />
      </a>
      <span>© {new Date().getFullYear()} Gamer Packaging, Inc.</span>
      <span>Customer Design Request Portal</span>
    </div>
  </footer>
);

const CustomerIntakePageV4 = () => {
  const [form, setForm] = useState({ ...emptyDesignRequestForm });
  const [skuItems, setSkuItems] = useState([blankSku()]);
  const [attachments, setAttachments] = useState([]);
  const [options, setOptions] = useState({ customers: [], manufacturers: [], isr_list: [], osr_list: [] });
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [draggingArtworkV49, setDraggingArtworkV49] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/public/intake-options`)
      .then((response) => setOptions(response.data || {}))
      .catch(() => setError("The form reference lists could not be loaded. Please refresh and try again."))
      .finally(() => setLoadingOptions(false));
  }, []);

  // intake-artwork-drag-drop-v49
  const isArtworkDropTargetV49 = (target) =>
    Boolean(
      target?.closest?.(".artwork-upload-panel")
    );

  const handleArtworkDragOverV49 = (event) => {
    if (
      uploadingFiles
      || !isArtworkDropTargetV49(event.target)
    ) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setDraggingArtworkV49(true);
  };

  const handleArtworkDragLeaveV49 = (event) => {
    const panel = event.target?.closest?.(
      ".artwork-upload-panel"
    );

    if (!panel) return;

    const next = event.relatedTarget;

    if (
      next
      && panel.contains(next)
    ) {
      return;
    }

    setDraggingArtworkV49(false);
  };

  const handleArtworkDropV49 = (event) => {
    if (!isArtworkDropTargetV49(event.target)) {
      return;
    }

    event.preventDefault();
    setDraggingArtworkV49(false);

    if (uploadingFiles) return;

    const files = Array.from(
      event.dataTransfer?.files || []
    );

    if (!files.length) return;

    uploadFiles(files);
  };

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploadingFiles(true);
    setError(null);
    try {
      const uploaded = [];
      for (const file of files) {
        const data = new FormData();
        data.append("file", file);
        const response = await axios.post(`${API}/api/public/intake-artwork`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(response.data);
      }
      setAttachments((previous) => [...previous, ...uploaded]);
      toast.success(`${uploaded.length} artwork file${uploaded.length === 1 ? "" : "s"} uploaded`);
    } catch (err) {
      setError(err.response?.data?.detail || "An artwork file could not be uploaded. Files over 25 MB should be zipped before upload.");
    } finally {
      setUploadingFiles(false);
    }
  };

  const validate = () => {
    const required = [
      [form.company_name, "Customer"],
      [form.contact_name, "Contact Name"],
      [form.contact_email, "Contact Email"],
      [form.manufacturer_name, "Manufacturer"],
      [form.isr, "Sales Ops"],
      [form.osr, "Outside Sales Rep (OSR)"],
      [form.can_size, "Can Size"],
      [form.can_finish, "End Size"],
    ];

    if (form.can_size === "Other") required.push([form.can_size_other, "Other Can Size"]);
    if (form.beverage_type === "Beer") required.push([form.beer_type, "Beer Type"]);

    skuItems.forEach((sku, index) => {
      required.push([sku.design_type, `SKU ${index + 1} Design Type`]);
      required.push([sku.inside_varnish, `SKU ${index + 1} Inside Varnish`]);
      if (sku.design_type === "Revision") {
        required.push([sku.conversion_type, `SKU ${index + 1} Conversion Type`]);
        required.push([sku.conversion_date, `SKU ${index + 1} Conversion Date`]);
      }
    });

    return required.filter(([value]) => !String(value || "").trim()).map(([, label]) => label);
  };

  const normalizeSku = (sku) => {
    const sampleFields = sku.sample_same_as_contact ? contactSampleFields(form) : sku;
    return {
      ...sku,
      ...sampleFields,
      sample_ship_to_address: [
        sampleFields.sample_ship_to_street,
        sampleFields.sample_ship_to_city,
        sampleFields.sample_ship_to_state,
        sampleFields.sample_ship_to_zip,
      ].filter(Boolean).join(", "),
      symbol: String(sku.symbol || "").trim() || "None",
      specialty_ink_type: sku.specialty_ink_type === "Other" ? sku.specialty_ink_other : sku.specialty_ink_type,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    const missing = validate();
    if (missing.length) {
      setError(`Required fields missing: ${missing.join(", ")}`);
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const projectFields = {
      ...form,
      customer_id: "",
      can_size: form.can_size === "Other" ? form.can_size_other : form.can_size,
    };

    const normalizedSkus = skuItems.map(normalizeSku);

    try {
      const response = await axios.post(`${API}/api/public/design-requests-v4`, {
        company_name: form.company_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        priority: form.priority,
        target_due_date: form.deadline,
        notes: form.comments,
        project_fields: projectFields,
        sku_items: normalizedSkus,
        attachment_ids: attachments.map((attachment) => attachment.id),
      });
      setConfirmation(response.data);
      toast.success("Design request submitted for Sales Ops review");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit design request");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ ...emptyDesignRequestForm });
    setSkuItems([blankSku()]);
    setAttachments([]);
    setConfirmation(null);
    setError(null);
  };

  if (confirmation) {
    return (
      <div className="dof-page customer-intake-branded">
        <GamerBrandHeader />
        <main className="gamer-form-shell">
          <section className="gamer-confirmation-card">
            <div className="gamer-confirmation-mark">✓</div>
            <h1>Design Request Submitted</h1>
            <p>The request has been routed to Sales Ops for review before it is sent to Graphics.</p>
            <div className="gamer-confirmation-number">{confirmation.confirmation_number}</div>
            <p className="text-sm text-muted-foreground">v{confirmation.form_version} • r{confirmation.submission_revision}</p>
            <p>Status: Awaiting Sales Ops Review</p>
            <div className="dof-note" style={{ margin: "18px 0", textAlign: "left" }}>
              <div className="dof-note-title">LIQUID SAMPLE REMINDER</div>
              <div className="dof-note-text">{LIQUID_SAMPLE_REMINDER}</div>
            </div>
            <button type="button" onClick={resetForm} className="gamer-primary-button">Submit Another Design Request</button>
          </section>
        </main>
        <GamerBrandFooter />
      </div>
    );
  }

  return (
    <div className="dof-page customer-intake-branded">
      <style>{`.customer-intake-form-body .dof-version-banner { display: none; }`}</style>
      <GamerBrandHeader />
      <main className="gamer-form-shell">
        <section className="gamer-form-hero">
          <div className="gamer-form-hero-icon" aria-hidden="true">✎</div>
          <div><h1>Design Order Form</h1><p>Customer Design Request</p></div>
          <aside className="gamer-help-card">
            <strong>Need help with your request?</strong>
            Our Graphics team is available to help.<br />
            <a href="mailto:graphics@gamerpackaging.com">graphics@gamerpackaging.com</a>
          </aside>
        </section>
      </main>

      {error && <div className="gamer-form-error">{error}</div>}

      <main className="gamer-form-shell" style={{ paddingTop: 0 }}>
        <form noValidate onSubmit={handleSubmit} className="dof-paper gamer-form-paper">
          <div className="gamer-form-paper-heading">
            <div>
              <h2>Customer Design Request</h2>
              <span>v{FORM_VERSION}</span>
            </div>
            <button type="submit" disabled={loading || uploadingFiles || loadingOptions} className="dof-submit-btn-lg">
              {loading ? "Submitting..." : "Submit Order"}
            </button>
          </div>

          <div
            className={`customer-intake-form-body${
              draggingArtworkV49
                ? " intake-dragging-artwork-v49"
                : ""
            }`}
            data-testid="intake-artwork-drop-host-v49"
            onDragEnter={handleArtworkDragOverV49}
            onDragOver={handleArtworkDragOverV49}
            onDragLeave={handleArtworkDragLeaveV49}
            onDrop={handleArtworkDropV49}
          >
            {loadingOptions ? (
              <div className="dof-note"><div className="dof-note-text">Loading form options...</div></div>
            ) : (
              <DesignRequestFormFields
                form={form}
                setForm={setForm}
                skuItems={skuItems}
                setSkuItems={setSkuItems}
                options={options}
                attachments={attachments}
                uploadingFiles={uploadingFiles}
                onFilesSelected={uploadFiles}
                onRemoveAttachment={(index) => setAttachments((previous) => previous.filter((_, itemIndex) => itemIndex !== index))}
              />
            )}
          </div>

          <div className="dof-note" style={{ marginTop: "24px" }}>
            <div className="dof-note-title">REVIEW PROCESS</div>
            <div className="dof-note-text">After submission, Sales Ops will add any required Artwork PO, review the request, and release it to Graphics.</div>
          </div>

          <div className="gamer-form-actions">
            <button type="submit" disabled={loading || uploadingFiles || loadingOptions} className="dof-submit-btn-lg">
              {loading ? "Submitting..." : "Submit Design Request"}
            </button>
          </div>
        </form>
      </main>
      <GamerBrandFooter />
    </div>
  );
};

export default CustomerIntakePageV4;